import React, { useCallback, useEffect, useState } from "react";
import styled from "@emotion/styled";
import { NavLink } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import AddTaxOffice from "../../../components/AddTaxOffice";
import {
  Grid,
  Link,
  Box,
  Breadcrumbs as MuiBreadcrumbs,
  Card as MuiCard,
  CardContent as MuiCardContent,
  Typography,
  Divider,
  IconButton,
} from "@mui/material";
import { Delete as DeleteIcon } from "@mui/icons-material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { spacing } from "@mui/system";
import Notification from "../../../components/Notification";
import ConfirmDialog from "../../../components/ConfirmDialog";
import axios from "axios";
import ViewTaxOfficeDialog from "../../../components/ViewTaxOfficeDialog";
import DataGridExport from "../../../components/DataGridExport";
import { getApiErrorMessage } from "../../../utils/taxOfficeUtils";

const Breadcrumbs = styled(MuiBreadcrumbs)(spacing);
const Card = styled(MuiCard)(spacing);
const CardContent = styled(MuiCardContent)(spacing);

const TaxOfficesDataGrid = () => {
  const [sortModel, setSortModel] = useState([{ field: "name", sort: "asc" }]);
  const [tableData, setTableData] = useState([]);
  const [notify, setNotify] = useState({
    isOpen: false,
    message: "",
    type: "",
  });
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: "",
    subTitle: "",
  });
  const [viewTaxOfficeDialog, setViewTaxOfficeDialog] = useState({
    isOpen: false,
    title: "",
    subTitle: "",
    params: {},
  });

  const columns = [
    { field: "taxOfficeId", headerName: "Id", width: 70, hide: true },
    { field: "name", headerName: "Name", flex: 1.2 },
    { field: "countryCode", headerName: "Country", flex: 0.4 },
    { field: "street", headerName: "Street", flex: 1 },
    { field: "buildingIdentifier", headerName: "Building", flex: 0.4 },
    { field: "postalCode", headerName: "Postal Code", flex: 0.5 },
    { field: "city", headerName: "City", flex: 0.7 },
    { field: "taxOfficeNumber", headerName: "Number", flex: 0.5 },
    {
      field: "actions",
      headerName: "Actions",
      flex: 0.5,
      renderCell: (params) => {
        return (
          <Box>
            <IconButton
              aria-label={`delete tax office ${params.row.name}`}
              onClick={() => {
                setConfirmDialog({
                  isOpen: true,
                  title: "Delete Tax Office",
                  subTitle: `Are you sure you want to delete ${params.row.name}?`,
                  onConfirm: () => {
                    handleDeleteConfirmed(params.row);
                  },
                });
              }}
            >
              <DeleteIcon />
            </IconButton>
            <IconButton
              aria-label={`view tax office ${params.row.name}`}
              onClick={() => {
                setViewTaxOfficeDialog({
                  isOpen: true,
                  title: "View Tax Office",
                  params: params.row,
                });
              }}
            >
              <VisibilityIcon />
            </IconButton>
          </Box>
        );
      },
    },
  ];

  const fetchTaxOffices = useCallback(async () => {
    try {
      const response = await axios.get("/api/v1/taxOffices");
      setTableData(response.data);
    } catch (error) {
      console.error("Error fetching tax offices:", error);
    }
  }, []);

  useEffect(() => {
    fetchTaxOffices();
  }, [fetchTaxOffices]);

  const handleDeleteConfirmed = (params) => {
    axios
      .delete(`/api/v1/taxOffices/${params.taxOfficeId}`)
      .then(() => {
        setNotify({
          isOpen: true,
          message: `${params.name} successfully deleted`,
          type: "success",
        });
        fetchTaxOffices();
      })
      .catch((error) => {
        setNotify({
          isOpen: true,
          message: getApiErrorMessage(error, "Cannot delete tax office"),
          type: "error",
        });
      });

    setConfirmDialog({ ...confirmDialog, isOpen: false });
  };

  return (
    <React.Fragment>
      <Helmet title="Tax Offices" />
      <Grid justifyContent="space-between" container spacing={10}>
        <Grid item>
          <Typography variant="h3" gutterBottom display="inline">
            Tax Offices
          </Typography>
          <Breadcrumbs aria-label="Breadcrumb" mt={2}>
            <Link component={NavLink} to="/">
              Dashboard
            </Link>
            <Link component={NavLink} to="/settings/globalsettings/taxoffices">
              Global Settings
            </Link>
            <Typography>Tax Offices</Typography>
          </Breadcrumbs>
        </Grid>
      </Grid>

      <Divider my={6} />
      <Card mb={0}>
        <CardContent
          pb={1}
          sx={{
            marginBottom: 0,
          }}
        >
          <Typography variant="h6" gutterBottom>
            Tax Offices Table
          </Typography>
          <Typography variant="body2" gutterBottom>
            In this table you can search, add and maintain tax offices. These
            offices can be selected on the customer card.
          </Typography>
        </CardContent>
        <Grid container spacing={0} mt={0}>
          <AddTaxOffice refreshTable={fetchTaxOffices} />
          <Notification notify={notify} setNotify={setNotify} />
          <ConfirmDialog
            confirmDialog={confirmDialog}
            setConfirmDialog={setConfirmDialog}
          />
          <ViewTaxOfficeDialog
            viewTaxOfficeDialog={viewTaxOfficeDialog}
            setViewTaxOfficeDialog={setViewTaxOfficeDialog}
            refreshTable={fetchTaxOffices}
          />
          <Grid item xs={12}>
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <DataGridExport tableData={tableData} columns={columns} />
            </div>
            <DataGrid
              getRowId={(row) => row.taxOfficeId}
              disableSelectionOnClick
              components={{ Toolbar: GridToolbar }}
              density="compact"
              componentsProps={{
                toolbar: {
                  csvOptions: { disableToolbarButton: false },
                  printOptions: { disableToolbarButton: false },
                  showQuickFilter: true,
                  quickFilterProps: { debounceMs: 250 },
                },
              }}
              rows={tableData}
              columns={columns}
              pageSize={15}
              sortModel={sortModel}
              onSortModelChange={setSortModel}
              initialState={{
                sorting: {
                  sortModel: [{ field: "name", sort: "asc" }],
                },
              }}
              sx={{
                height: 667,
                width: "100%",
                borderRadius: 1,
                backgroundColor: "background.paper",
                boxShadow: 2,
                "& .MuiDataGrid-cell:hover": {
                  color: "primary.main",
                },
                marginTop: 0,
              }}
            />
          </Grid>
        </Grid>
      </Card>
    </React.Fragment>
  );
};

export default TaxOfficesDataGrid;
